import React, { useState, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid2,
  Paper,
  InputAdornment,
  InputProps2,
} from "@mui/material";
import { styled } from "@mui/system";

// Icons
import { ReactComponent as UserStoryIcon } from "../../images/userStoryIcon.svg";
import { ReactComponent as TestStrategyIcon } from "../../images/testStrategyIcon.svg";
import { ReactComponent as TestPlanIcon } from "../../images/testPlanIcon.svg";
import { ReactComponent as TestDesignIcon } from "../../images/testDesignIcon.svg";
import { ReactComponent as TestAutoIcon } from "../../images/testAutomationIcon.svg";
import { ReactComponent as BugReportIcon } from "../../images/bugReportingIcon.svg";
import { ReactComponent as RiskIcon } from "../../images/riskAssessmentIcon.svg";
import { ReactComponent as CoverageIcon } from "../../images/coverageAnalysisIcon.svg";
import { ReactComponent as TestDataCreationIcon } from "../../images/testDataCreationIcon.svg";
import { ReactComponent as ScriptIcon } from "../../images/scriptMaintenanceIcon.svg";
import { ReactComponent as TestMetricsIcon } from "../../images/testMetricsIcon.svg";
import { ReactComponent as SummaryReportsIcon } from "../../images/summaryReportIcon.svg";
import { ReactComponent as SecurityTestingIcon } from "../../images/securityTestingIcon.svg";
import { ReactComponent as APITestingIcon } from "../../images/APITestingIcon.svg";
import { ReactComponent as PromptLibIcon } from "../../images/promptLibraryIcon.svg";
import { ReactComponent as PerformanceTestingIcon } from "../../images/performanceTestingIcon.svg";
import { ReactComponent as ETLIcon } from "../../images/ETLIcon.svg";
import { ReactComponent as FrameworkShiftIcon } from "../../images/FrameworkShiftIcon.svg";
import { ReactComponent as LocatorsEngine } from "../../images/locatorsengine.svg";

import NavigationRail from "../common-components/NavigationRail";
import { useNavigateWithKey } from "../../custom-hooks/navigateWithKey";

const featureItems = [
  { title: "QE Strategy", desc: "Transforming Enterprise Quality", icon: <TestStrategyIcon />, path: "/dashboard/qe-strategy/home-page" },
  { title: "Test Plan", desc: "Craft a detailed roadmap", icon: <TestPlanIcon />, path: "/dashboard/test-plan" },
  { title: "User Story", desc: "Refine user stories", icon: <UserStoryIcon />, path: "/dashboard/userstory/input" },
  { title: "Risk Assessment", desc: "Evaluate user story risk", icon: <RiskIcon />, path: "" },
  { title: "Test Design", desc: "Design with purpose", icon: <TestDesignIcon />, path: "/dashboard/generate-testcases/input" },
  { title: "Coverage Analysis", desc: "Maximize test coverage", icon: <CoverageIcon />, path: "" },
  { title: "Test Data Creation", desc: "Generate comprehensive test data", icon: <TestDataCreationIcon />, path: "" },
  { title: "Test Automation", desc: "Create scripts with ease", icon: <TestAutoIcon />, path: "/dashboard/test-automation" },
  { title: "Bug Reporting", desc: "Simplify defect reporting", icon: <BugReportIcon />, path: "/dashboard/bugreportenhancer/input" },
  { title: "Script Maintenance", desc: "Organize test scripts", icon: <ScriptIcon />, path: "" },
  { title: "Test Metrics", desc: "Measure test effectiveness", icon: <TestMetricsIcon />, path: "" },
  { title: "Summary Reports", desc: "Quick test summaries", icon: <SummaryReportsIcon />, path: "" },
  { title: "Security Testing", desc: "Identify vulnerabilities", icon: <SecurityTestingIcon />, path: "/dashboard/security-testing/home-page" },
  { title: "API Testing", desc: "Test APIs with ease", icon: <APITestingIcon />, path: "/dashboard/api-testing/landingpage" },
  { title: "Prompt Library", desc: "AI testing prompts", icon: <PromptLibIcon sx={{ fill: "" }} />, path: "" },
  { title: "Performance Testing", desc: "Stress and load testing", icon: <PerformanceTestingIcon />, path: "/dashboard/performance-testing" },
  { title: "ETL Testing", desc: "Verify data pipelines", icon: <ETLIcon />, path: "" },
  { title: "Framework Shift", desc: "Seamless automation migration", icon: <FrameworkShiftIcon />, path: "" },
  // { title: "Locators Engine", desc: "Generation of Locators", icon: <LocatorsEngine/>, path: "/dashboard/cag-locators/cag-locators-generation" },
];

// Gradient Typography Styling
const GradientTypography = styled(Typography)(({ theme }) => ({
  background: "linear-gradient(94deg, #0074ff 11.09%, #e24e5f 99.55%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
}));

const Dashboard = () => {
  const navigate = useNavigateWithKey();
  const [searchQuery, setSearchQuery] = useState("");

  // Filter features based on searchQuery
  const filteredItems = featureItems.filter(
    (item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.desc.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: "#F6F3F2",
      }}
    >
      <Grid2
        container
        spacing={2}
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexWrap: "nowrap",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* NavigationRail Section */}
        <Grid2
          item
          xs={12}
          md={2}
          sx={{
            display: { xs: "none", md: "block" },
          }}
        >
          <NavigationRail />
        </Grid2>

        <Box
          sx={{
            minHeight: "100vh",
            background: "linear-gradient(FFFFFF, #f4f4f4)",
            maxWidth: "100%",
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              textAlign: "center",
              mt: 6,
              mb: 4,
              mx: "auto",
              maxWidth: "100%",
              width: "650px",
            }}
          >
            <GradientTypography
              variant="h3"
              component="h1"
              sx={{
                mb: 2,
                fontWeight: 400,
                fontSize: { xs: "32px", md: "57px" },
                lineHeight: "64px",
              }}
            >
              Your Partner for Intelligent QE
            </GradientTypography>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                fontSize: { xs: "12px", md: "14px" },
              }}
            >
              An all-in-one AI solution for seamless software testing — <br />
              from user stories to test cases, automation, reports, and
              non-functional testing.
            </Typography>
          </Box>

          {/* Tools Section */}
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 4,
              fontSize: { xs: "12px", md: "14px" },
              position: "relative",
              top: "-15px",
            }}
          >
            <b>What can I help with?</b>
          </Typography>

          {/* Search Field */}
          {/* <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}> */}
          {/* <TextField
              placeholder="Search"
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: { xs: "90%", sm: 400 } }}
            />

          </Box> */}

          <Grid2
            container
            spacing={3}
            justifyContent="center"
            wrap="wrap"
            sx={{
              ml: 9,
              mb: 2,
            }}
          >
            {filteredItems.length === 0 ? (
              <Typography
                variant="body2"
                textAlign="center"
                color="text.secondary"
              >
                No tools match your search.
              </Typography>
            ) : (
              filteredItems.map((item, index) => (
                <Grid2 item key={index}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      textAlign: "center",
                      cursor: "pointer",
                      transition: "transform 0.2s",
                      height: 100,
                      width: 220,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        backgroundColor: "#e3f2fd",
                      },
                    }}
                    onClick={() => navigate(item.path)}
                  >
                    <Box
                      height={60}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      mb={1}
                    >
                      {item.icon}
                    </Box>
                    <Box flexGrow={1}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.desc}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid2>
              ))
            )}
          </Grid2>
        </Box>
      </Grid2>
    </Box>
  );
};

export default Dashboard;